import { useState } from "react";
import {
  Alert,
  Button,
  Card,
  Grid2,
  Stack,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { IPlaceSchedule } from "../../../../Domain/Types/FloorPlan/PlaceSchedule";
import { IZoneSchedule } from "../../../../Domain/Types/FloorPlan/ZoneSchedule";
import { BookingInputs } from "../../../../Domain/Types/BookingInputs.type";
import { generateBtnText } from "./SelectionPrompt.functions";
import { ScheduleContentList } from "../../../../../Schedule/ScheduleCalendar/ScheduleSelectionDialog/ScheduleContentList.component";
import { ShowMoreContentIcon } from "./partial/ShowMoreContainerIcon.component";
import { SelectionPromptHeader } from "./partial/SelectionPromptHeader.component";
import { ShowMoreDescription } from "../../../../../Schedule/ScheduleCalendar/ScheduleSelectionDialog/ShowMoreDescription.component";
import { BookingType } from "../../../../../../features/Booking-Form/typings/booking-inputs";
import { IFloorPayload } from "../../../../Domain/Types/FloorPlan/FloorPayload.type";
import { BookingServicePrompt } from "../BookingServicePrompt/BookingServicePrompt.component";
import { useSelectionPrompt2ConferenceState } from "./useSelectionPrompt2ConferenceState";

interface Props {
  selected: { place: IPlaceSchedule | undefined; zone: IZoneSchedule | undefined };
  /** true when the selected place is available, false when it is occupied/disabled/not permitted */
  selectableByVariant: boolean;
  bookingInputs: BookingInputs;
  floorPlanForService: IFloorPayload | undefined;
  /** Current floor inventory id (for booking favorites) */
  floorInventoryId: number;
  userIndex: number;
  userId: string;
  selectedZone?: number;
  isOccupiedDataLoading: boolean;
  onConfirm: () => void;
  onClose: (e: any) => void;
  openTimeDialog: (selectedEntryId: number) => void;
}

function getContentChildItemStyle(showMore: boolean) {
  return { flexWrap: "nowrap", overflowY: "auto", maxHeight: showMore ? "200px" : "unset" };
}

function getParentCardStyles(isBrowser: boolean, isMobile: boolean) {
  return {
    overflowY: "auto",
    p: isBrowser ? 3 : 2,
    minWidth: isBrowser ? 500 : "fit-content",
    maxWidth: isBrowser ? 500 : "unset",
    maxHeight: isMobile ? "85vh" : "80vh"
  };
}

/**
 * new selection prompt in the booking process
 * @version 0.1.1
 */
export function SelectionPrompt2({
  selected,
  selectableByVariant = true,
  bookingInputs,
  floorPlanForService,
  floorInventoryId,
  userIndex,
  userId,
  selectedZone,
  isOccupiedDataLoading,
  onConfirm,
  onClose,
  openTimeDialog
}: Readonly<Props>) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isBrowser = useMediaQuery(theme.breakpoints.up(1200));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    activateBookingServices,
    selectedEntry,
    isServiceAdminBypass,
    remoteMaxSetupTime,
    isServiceModalOpen,
    setIsServiceModalOpen,
    isBookingServiceLoading,
    setIsBookingServiceLoading,
    derived
  } = useSelectionPrompt2ConferenceState({
    selected,
    bookingInputs,
    floorPlanForService,
    selectedZone,
    userId,
    selectableByVariant
  });

  const [showMoreDescription, setShowMoreDescription] = useState(true);
  const [showMoreEquipment, setShowMoreEquipment] = useState(false);
  const [showMoreEquipCategory, setShowMoreEquipCategory] = useState(false);
  const [showMoreBookProp, setShowMoreBookProp] = useState(false);
  const [showMoreCategory, setShowMoreCategory] = useState(false);

  const parentCardStyles = getParentCardStyles(isBrowser, isMobile);

  return (
    <Card data-testid="selection-prompt2-card" sx={parentCardStyles}>
      {selectedEntry && derived && (
        <Stack direction={"column"} spacing={2} data-testid="selection-prompt">
          {/* selection prompt header */}
          <SelectionPromptHeader
            isConferenceZone={derived.isConferenceZone}
            selectedEntry={selectedEntry}
            isOccupiedDataLoading={isOccupiedDataLoading}
            openTimeDialog={() =>
              openTimeDialog(selectedEntry.inventoryId || selectedEntry.inventory?.id || 0)
            }
            onClose={onClose}
            isMandatoryForService={derived.hasMandatory}
            isServiceExisting={derived.hasOrderableService}
            isConferenceServicesActionEnabled={derived.isConferenceServicesActionEnabled}
            mandatoryServicesFulfilled={derived.mandatoryServicesFulfilled || isServiceAdminBypass}
            isServiceAdminBypass={isServiceAdminBypass}
            isBookingServiceLoading={isBookingServiceLoading}
            setIsServiceModalOpen={
              bookingInputs.mode === BookingType.CONFERENCEZONE && activateBookingServices
                ? setIsServiceModalOpen
                : undefined
            }
            favoriteFloorInventoryId={floorInventoryId}
            favoriteBookingType={bookingInputs.mode ?? bookingInputs.bookingType}
          />

          {/* description */}
          <ScheduleContentList
            contentIcon={
              <ShowMoreContentIcon
                type={"Description"}
                showMoreContent={showMoreDescription}
                setShowMoreContent={setShowMoreDescription}
              />
            }
            contentTitle={t("Description")}
            contents={[]}
            contentChildren={
              <Grid2 container rowGap={1} flexDirection={"column"}>
                <Grid2
                  sx={[
                    { whiteSpace: "pre-line" },
                    showMoreDescription ? { overflowY: "scroll" } : { overflowY: "hidden" },
                    showMoreDescription ? { maxHeight: "150px" } : { maxHeight: "30px" }
                  ]}
                >
                  {selectedEntry.description}
                </Grid2>
              </Grid2>
            }
          />

          {/* equipment list */}
          {selectedEntry.equipments && selectedEntry.equipments.length > 0 && (
            <ScheduleContentList
              contentIcon={
                <ShowMoreContentIcon
                  type={"Equipment"}
                  showMoreContent={showMoreEquipment}
                  setShowMoreContent={setShowMoreEquipment}
                />
              }
              contentTitle={t("Equipment")}
              contents={[]}
              contentChildren={
                <Grid2
                  container
                  rowGap={1}
                  flexDirection={"column"}
                  sx={{ ...getContentChildItemStyle(showMoreEquipment) }}
                >
                  {selectedEntry.equipments.map((equip, index) => {
                    const length = selectedEntry.equipments.length;
                    return (
                      <div key={equip.id}>
                        <Grid2 pb={0.5}>{equip.name}</Grid2>
                        {showMoreEquipment && (
                          <ShowMoreDescription
                            description={equip.description ?? null}
                            index={index}
                            length={length}
                            spaceStyleY={1}
                          />
                        )}
                      </div>
                    );
                  })}
                </Grid2>
              }
            />
          )}

          {/* equipment category list */}
          {selectedEntry.equipmentCategories && selectedEntry.equipmentCategories.length > 0 && (
            <ScheduleContentList
              contentIcon={
                <ShowMoreContentIcon
                  type={"Equipment Category"}
                  showMoreContent={showMoreEquipCategory}
                  setShowMoreContent={setShowMoreEquipCategory}
                />
              }
              contentTitle={t("Equipment")}
              contents={[]}
              contentChildren={
                <Grid2
                  container
                  rowGap={1}
                  flexDirection={"column"}
                  sx={{ ...getContentChildItemStyle(showMoreEquipCategory) }}
                >
                  {selectedEntry.equipmentCategories.map((equipCate, index) => {
                    const length = selectedEntry.equipmentCategories?.length ?? 0;

                    return (
                      <div key={equipCate.id}>
                        <Grid2 container>
                          <Grid2 size={{ xs: 9 }} pb={0.5}>
                            {equipCate.name}
                          </Grid2>
                          {showMoreEquipCategory && (
                            <Grid2 size={{ xs: 2 }} display={"flex"} sx={{ placeContent: "end" }}>
                              {t("Qty")}
                              <Typography sx={{ pl: 1 }}>{equipCate.quantity}</Typography>
                            </Grid2>
                          )}
                        </Grid2>
                        {showMoreEquipCategory && (
                          <ShowMoreDescription
                            description={equipCate.description}
                            index={index}
                            length={length}
                            spaceStyleY={1}
                          />
                        )}
                      </div>
                    );
                  })}
                </Grid2>
              }
            />
          )}

          {/* properties list */}
          {selectedEntry.bookingProperties && selectedEntry.bookingProperties?.length > 0 && (
            <ScheduleContentList
              contentIcon={
                <ShowMoreContentIcon
                  type={"BookingProperty"}
                  showMoreContent={showMoreBookProp}
                  setShowMoreContent={setShowMoreBookProp}
                />
              }
              contentTitle={t("Property")}
              contents={[]}
              contentChildren={
                <Grid2
                  container
                  rowGap={1}
                  flexDirection={"column"}
                  sx={{ ...getContentChildItemStyle(showMoreBookProp) }}
                >
                  {selectedEntry.bookingProperties.map((prop, index) => {
                    const length = selectedEntry.bookingProperties?.length ?? 0;
                    return (
                      <div key={prop.id}>
                        <Grid2 pb={0.5}>{prop.name}</Grid2>
                        {showMoreBookProp && (
                          <ShowMoreDescription
                            description={prop.description}
                            index={index}
                            length={length}
                            spaceStyleY={1}
                          />
                        )}
                      </div>
                    );
                  })}
                </Grid2>
              }
            />
          )}

          {/* category */}
          {selectedEntry.category && (
            <ScheduleContentList
              contentIcon={
                <ShowMoreContentIcon
                  type={"Category"}
                  showMoreContent={showMoreCategory}
                  setShowMoreContent={setShowMoreCategory}
                />
              }
              contentTitle={t("Category")}
              contents={[]}
              contentChildren={
                <Grid2 container rowGap={1} flexDirection={"column"}>
                  <Grid2>{selectedEntry.category?.name}</Grid2>
                  {showMoreCategory && <Grid2 pl={2}>{selectedEntry.category?.description}</Grid2>}
                </Grid2>
              }
            />
          )}

          {/* confirm button */}
          <Button
            disabled={derived.disableConfirmBooking}
            data-testid={"select-pl-btn"}
            onClick={onConfirm}
          >
            {generateBtnText(
              selectableByVariant,
              bookingInputs,
              selectedEntry.approvalRequired,
              userIndex,
              userId,
              undefined,
              t
            )}
          </Button>
        </Stack>
      )}

      {!selectedEntry && <Alert variant={"filled"} severity={"error"} title={"Oops"} />}

      <BookingServicePrompt
        userId={userId}
        bookingInputs={bookingInputs}
        floorPlanForService={floorPlanForService}
        selectedZone={selectedZone}
        selectedEntry={selectedEntry}
        isServiceAdminBypass={isServiceAdminBypass}
        remoteMaxSetupTime={remoteMaxSetupTime}
        isModalOpen={isServiceModalOpen}
        setIsModalOpen={setIsServiceModalOpen}
        isBookingServiceLoading={isBookingServiceLoading}
        setIsBookingServiceLoading={setIsBookingServiceLoading}
      />
    </Card>
  );
}
